import {
    faArrowRight,
    faCompress,
    faExpand,
    faForward,
    faPause,
    faPlay,
    // faPlus,
    faStop,
    faVolumeMute,
    faVolumeUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useInterval from "@use-it/interval";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Container, Form } from "react-bootstrap";
import { useFullScreenHandle } from "react-full-screen";
import { loadConfig, saveConfig } from "../common/config";
import { DEFAULT_TIME_PER_ATHLETE } from "../common/constants";
import { isSpeechSupported, speakCommand } from "../common/speech";
import { getMajorityTime, secondsToString } from "../common/util";
import { useWakeLock } from "../hooks/useWakeLock";
import { Round, State } from "../types";
import { AthletesSettings } from "./AthletesSettings";
// import { CopyButton } from "./CopyButton";
import { Countdown } from "./Countdown";
// import { DiscordBot } from "./DiscordBot";
import { Footer } from "./Footer";
// import { Header } from "./Header";

const initialConfig = loadConfig();

export default function App() {
    const [state, setState] = useState<State>("stopped");

    const {
        node: fullscreenRef,
        active: fullscreenActive,
        enter: enterFullscreen,
        exit: leaveFullscreen,
    } = useFullScreenHandle();

    useWakeLock(state === "running" || state === "paused");

    const [startDelay, setStartDelay] = useState(initialConfig.startDelay);
    const [speechEnabled, setSpeechEnabled] = useState(initialConfig.speechEnabled);
    const [selectedVoice, setselectedVoice] = useState(initialConfig.selectedVoice);
    const [athletes, setAthletes] = useState(initialConfig.athletes);

    const [round, setRound] = useState<Round | undefined>(undefined);

    // Keep a ref to athletes so interval callback always has latest value
    const athletesRef = useRef(athletes);
    useEffect(() => {
        athletesRef.current = athletes;
    }, [athletes]);

    const getAthleteName = (athleteId: string) => {
        const athlete = athletes.find(a => a.id === athleteId);
        if (!athlete) {
            return "";
        }
        return athlete.text || `Athlete ${athletes.indexOf(athlete) + 1}`;
    };

    useEffect(() => {
        saveConfig({ athletes, startDelay, speechEnabled, selectedVoice });
    }, [athletes, startDelay, speechEnabled, selectedVoice]);

    // Function to calculate next athlete - uses athletesRef for latest data
    const getNextAthleteId = useCallback((currentAthleteId: string | undefined) => {
        const currentAthletes = athletesRef.current;
        
        if (currentAthleteId === undefined) {
            const firstEnabled = currentAthletes.find((a) => a.enabled);
            return firstEnabled ? firstEnabled.id : currentAthletes[0].id;
        }

        // Find the current athlete's position
        const currentAthleteIndex = currentAthletes.findIndex(a => a.id === currentAthleteId);
        
        // Find the next enabled athlete after the current athlete's position
        const nextEnabled = [...currentAthletes.slice(currentAthleteIndex + 1), ...currentAthletes]
            .find(a => a.enabled);
        
        return nextEnabled ? nextEnabled.id : currentAthletes[0].id;
    }, []);

    // Memoized value for display purposes (uses state directly)
    const nextAthlete = useMemo(() => {
        return getNextAthleteId(round?.currentAthleteId);
    }, [round, athletes, getNextAthleteId]);

    const speak = (command: string) => {
        if (!speechEnabled) {
            return;
        }

        speakCommand(command, {
            nextAthlete: getAthleteName(nextAthlete),
            started: round?.currentAthleteId !== undefined,
        });
    };

    const prevTimeRef = useRef<number | undefined>();
    useInterval(() => {
        const now = Date.now();
        const timeDiff = (now - prevTimeRef.current!) / 1_000;
        prevTimeRef.current = now;

        if (state !== "running" || round === undefined) {
            return;
        }

        let updatedRound: Round = {
            ...round,
            timePassed: round.timePassed + timeDiff,
        };

        if (Math.floor(round.timePassed) !== Math.floor(updatedRound.timePassed)) {
            const remainingTime = updatedRound.totalTime - updatedRound.timePassed;
            const remainingTimeInSeconds = Math.floor(Math.max(0, remainingTime));

            speak(remainingTimeInSeconds.toString());

            if (remainingTimeInSeconds === 0) {
                const nextAthleteId = getNextAthleteId(round.currentAthleteId);
                const nextAthleteObj = athletesRef.current.find(a => a.id === nextAthleteId);
                updatedRound = {
                    timePassed: -remainingTime,
                    currentAthleteId: nextAthleteId,
                    totalTime: nextAthleteObj ? nextAthleteObj.time : DEFAULT_TIME_PER_ATHLETE,
                };
            }
        }

        setRound(updatedRound);
    }, 500);

    const handleSkip = () => {
        speak("skip");
        const nextAthleteObj = athletes.find(a => a.id === nextAthlete);
        setRound({
            timePassed: 0,
            currentAthleteId: nextAthlete,
            totalTime: nextAthleteObj ? nextAthleteObj.time : DEFAULT_TIME_PER_ATHLETE,
        });
    };

    const handleStart = () => {
        const now = Date.now();
        prevTimeRef.current = now;

        const firstAthleteObj = athletes.find(a => a.id === nextAthlete);
        setRound({
            timePassed: 0,
            totalTime: startDelay > 0 ? startDelay : (firstAthleteObj ? firstAthleteObj.time : DEFAULT_TIME_PER_ATHLETE),
            currentAthleteId: startDelay > 0 ? undefined : nextAthlete,
        });

        setAthletes((athletes) => athletes.map((a) => ({ ...a, enabled: true })));

        setState("running");
    };

    const handleStop = () => {
        setState("stopped");
        setAthletes((athletes) => athletes.map((a) => ({ ...a, enabled: true })));
    };

    const handlePause = () => {
        setState("paused");
    };

    const handleResume = () => {
        setState("running");
    };

    const handlePlusTime = (seconds: number) => {
        if (!round) {
            return;
        }

        setRound({
            ...round,
            totalTime: round.totalTime + seconds,
        });

        if ((round.totalTime + seconds) - round.timePassed < 0) {
            handleSkip()
        }
    };

    const handlePlusTimeAll = (seconds: number) => {
        setAthletes((athletes) => {
            // First, find the majority time among all athletes
            const majorityTime = getMajorityTime(athletes);
            // Then reset all athletes to that time and apply the adjustment
            return athletes.map((a) => ({
                ...a,
                time: Math.max(5, majorityTime + seconds),
            }));
        });
    };

    const isActiveState = (s: State): s is "running" | "paused" => s === "running" || s === "paused";

    return (
        <>
            {/* <Header /> */}

            <Container>
                <div
                    className="jumbotron pt-4 mb-2 position-relative d-flex flex-column align-items-center justify-content-center"
                    ref={fullscreenRef}>
                    {state !== "stopped" && round !== undefined ? (
                        <div className="w-100">
                            <div className="d-flex align-items-center justify-content-center gap-3">
                                <h2 className="mb-0 h4">
                                    {round.currentAthleteId === undefined ? "Lead-in" : getAthleteName(round.currentAthleteId)}
                                </h2>
                                <h2 className="mb-0 h2">
                                    <Countdown timePassed={round.timePassed} totalTime={round.totalTime} />
                                </h2>
                                <h2 className="mb-0 h5 text-muted">
                                    <FontAwesomeIcon icon={faArrowRight} /> {getAthleteName(nextAthlete)}
                                </h2>
                            </div>
                            <div className="row d-flex justify-content-center">
                                <div className="mt-3 text-center" style={{ maxWidth: "500px" }}>
                                    <AthletesSettings athletes={athletes} onChange={setAthletes} state={state} currentAthleteId={round.currentAthleteId} onPlusTimeAll={handlePlusTimeAll} />
                                </div>
                            </div>

                            <div className="mt-4 text-center">
                                {state === "paused" ? (
                                    <Button variant="info" className="me-1" onClick={handleResume}>
                                        <FontAwesomeIcon icon={faPlay} /> Resume
                                    </Button>
                                ) : null}

                                {state === "running" && (
                                    <div className="btn-toolbar justify-content-center" role="toolbar" aria-label="Manage Time Toolbar">
                                        <div className="btn-group" role="group" aria-label="Manage Time">
                                            <Button variant="info" className="btn btn-secondary" onClick={handlePause}>
                                                <FontAwesomeIcon icon={faPause} /> <span className="btn-text-responsive">Pause</span>
                                            </Button>
                                            <button type="button" className="btn btn-secondary toolbar-btn-15" onClick={() => handlePlusTime(-15)}>-15</button>
                                            <button type="button" className="btn btn-secondary toolbar-btn-10" onClick={() => handlePlusTime(-10)}>-10</button>
                                            <button type="button" className="btn btn-secondary" onClick={() => handlePlusTime(-5)}>-5</button>
                                            <span className="btn toolbar-spacer" style={{ cursor: 'default', backgroundColor: '#6c757d', color: '#999', padding: '6px 4px' }}>|</span>
                                            <button type="button" className="btn btn-secondary" onClick={() => handlePlusTime(5)}>+5</button>
                                            <button type="button" className="btn btn-secondary toolbar-btn-10" onClick={() => handlePlusTime(10)}>+10</button>
                                            <button type="button" className="btn btn-secondary toolbar-btn-15" onClick={() => handlePlusTime(15)}>+15</button>
                                            <Button variant="warning" className="btn btn-secondary" onClick={handleSkip}>
                                                <span className="btn-text-responsive">{round.currentAthleteId === undefined ? "Start now" : "Skip "}</span> <FontAwesomeIcon icon={faForward} />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-3 text-center">
                                {isActiveState(state) && (
                                    <Button variant="danger" size="sm" onClick={handleStop}>
                                        <FontAwesomeIcon icon={faStop} /> Stop Timer
                                    </Button>
                                )}

                                {isSpeechSupported && (
                                    <>
                                        {speechEnabled ? (
                                            <Button
                                                className="ms-2"
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => setSpeechEnabled(false)}
                                            >
                                                <FontAwesomeIcon icon={faVolumeUp} />
                                            </Button>
                                        ) : (
                                            <Button
                                                className="ms-2"
                                                size="sm"
                                                variant="danger"
                                                onClick={() => setSpeechEnabled(true)}
                                            >
                                                <FontAwesomeIcon icon={faVolumeMute} />
                                            </Button>
                                        )}
                                    </>
                                )}

                                {document.fullscreenEnabled && (
                                    <>
                                        {fullscreenActive ? (
                                            <Button className="ms-2" size="sm" variant="danger" onClick={leaveFullscreen}>
                                                <FontAwesomeIcon icon={faCompress} />
                                            </Button>
                                        ) : (
                                            <Button className="ms-2" size="sm" variant="secondary" onClick={enterFullscreen}>
                                                <FontAwesomeIcon icon={faExpand} />
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center mb-4">
                            <Button variant="primary" onClick={handleStart} size="lg">
                                <FontAwesomeIcon icon={faPlay} /> Start{" "}
                                {startDelay > 0 && `in ${secondsToString(startDelay)}`}
                            </Button>
                        </div>
                    )}

                    <Card className="mb-2 position-relative d-flex flex-column align-items-center justify-content-center">
                        <Card.Body hidden={state !== "stopped"}>
                            <h3>Settings</h3>
                            
                            <Form.Group controlId="startDelay">
                                <Form.Label>
                                    Start Delay (in seconds)
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    value={startDelay}
                                    onChange={(e) => setStartDelay(+e.target.value)}
                                    min={0}
                                    step={30}
                                />
                            </Form.Group>

                            {state === "stopped" && (
                                <div className="mt-5" style={{ maxWidth: "500px" }}>
                                    <AthletesSettings athletes={athletes} onChange={setAthletes} state={state} />
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Footer />
                </div>
            </Container>
        </>
    );
}